/**
 * EdgeSwipeCatcher — native-app-style left-edge swipe to open the mobile drawer.
 *
 * • Invisible 18 px strip pinned to the left edge (mobile only, z below overlay)
 * • Tracks touchmove via refs (no React state during gesture → 60 fps)
 * • Shows a subtle red glow that grows with drag distance
 * • Fires onOpen() + haptic when the user drags ≥ 60 px
 * • Respects vertical scrolling (touch-action: pan-y) so accidental edge
 *   contact during scroll doesn't hijack the gesture
 */
import { useRef, useEffect } from 'react';

const TRIGGER_PX = 60;   // horizontal distance to commit
const INDICATOR_MAX_PX = 80;

export default function EdgeSwipeCatcher({ onOpen, disabled }) {
  const indicatorRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const active = useRef(false);
  const committed = useRef(false);

  const paint = (px) => {
    const el = indicatorRef.current;
    if (!el) return;
    if (px <= 0) {
      el.style.opacity = '0';
      el.style.width = '0px';
      return;
    }
    const ratio = Math.min(1, px / INDICATOR_MAX_PX);
    el.style.opacity = '1';
    el.style.width = `${ratio * INDICATOR_MAX_PX}px`;
    el.style.background = `linear-gradient(90deg, rgba(205,4,11,${0.15 + ratio * 0.3}) 0%, rgba(205,4,11,${ratio * 0.15}) 50%, transparent 100%)`;
    el.style.boxShadow = `0 0 ${24 * ratio}px rgba(205,4,11,${0.3 * ratio})`;
  };

  const reset = () => {
    active.current = false;
    committed.current = false;
    paint(0);
  };

  const onStart = (e) => {
    if (disabled) return;
    const t = e.touches[0];
    if (!t || t.clientX > 18) return;
    active.current = true;
    committed.current = false;
    startX.current = t.clientX;
    startY.current = t.clientY;
  };

  const onMove = (e) => {
    if (!active.current) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - startX.current;
    const dy = Math.abs(t.clientY - startY.current);
    // Abort if user is clearly scrolling vertically
    if (dy > 24 && dy > dx) {
      reset();
      return;
    }
    if (dx > 0) paint(dx);
    if (!committed.current && dx > TRIGGER_PX) {
      committed.current = true;
      try { navigator.vibrate?.(10); } catch { /* noop */ }
      onOpen?.();
      reset();
    }
  };

  const onEnd = () => { if (active.current) reset(); };

  // Attach native (non-passive) listeners so preventDefault works on Android
  // Chrome — keeps horizontal swipes from accidentally scrolling the page.
  useEffect(() => {
    const strip = document.getElementById('cs-edge-swipe-strip');
    if (!strip) return;
    const handleStart = (e) => onStart(e);
    const handleMove = (e) => {
      if (active.current) {
        // prevent vertical nudge from hijacking the horizontal gesture
        const t = e.touches[0];
        const dx = t ? t.clientX - startX.current : 0;
        if (dx > 8) e.preventDefault();
        onMove(e);
      }
    };
    const handleEnd = () => onEnd();
    strip.addEventListener('touchstart', handleStart, { passive: true });
    strip.addEventListener('touchmove',  handleMove,  { passive: false });
    strip.addEventListener('touchend',   handleEnd,   { passive: true });
    strip.addEventListener('touchcancel',handleEnd,   { passive: true });
    return () => {
      strip.removeEventListener('touchstart', handleStart);
      strip.removeEventListener('touchmove',  handleMove);
      strip.removeEventListener('touchend',   handleEnd);
      strip.removeEventListener('touchcancel',handleEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, onOpen]);

  return (
    <>
      <div
        id="cs-edge-swipe-strip"
        aria-hidden
        className="fixed top-0 left-0 bottom-0 w-[18px] z-40 lg:hidden"
        style={{ touchAction: 'pan-y' }}
      />
      <div
        ref={indicatorRef}
        aria-hidden
        className="fixed top-0 left-0 bottom-0 z-40 pointer-events-none lg:hidden"
        style={{
          width: 0,
          opacity: 0,
          transition: 'opacity 160ms ease, width 160ms ease, box-shadow 160ms ease',
          willChange: 'width, opacity',
        }}
      />
    </>
  );
}
